import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { endpoints } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { QueueRule } from '@rossum/api-client/schemas';
import { useApiClient } from '../../../lib/apiClient';
import { ruleManagerPath } from '../../rule-manager/routes';
import { notify } from '../../toaster';
import { RULES_QUERY_KEY } from './rulesQueryKeys';
import { useDeleteRule } from './useDeleteRule';

export const useHandleDuplicateRule = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { mutate: deleteRule } = useDeleteRule();

  return useMutation({
    onSuccess: (duplicatedRule, params) => {
      queryClient.invalidateQueries([RULES_QUERY_KEY]);
      const toastId = `undo-duplicate-rule-${duplicatedRule.id}`;
      notify.default({
        title: intl.formatMessage({
          id: 'features.queueSettings.rules.list.duplicate.notification.success',
        }),
        toastActions: [
          {
            to: params.queue
              ? `/queues/${params.queue.id}/settings/rules/${duplicatedRule.id}/detail`
              : `${ruleManagerPath()}/${duplicatedRule.id}/detail`,
            label: intl.formatMessage({
              id: 'components.toaster.actions.goToQueue',
            }),
            onClick: () => {
              toast.dismiss(toastId);
            },
          },
          {
            label: intl.formatMessage({
              id: 'components.toaster.actions.undo',
            }),
            onClick: () => {
              toast.dismiss(toastId);
              deleteRule(
                { deletedRuleId: duplicatedRule.id },
                {
                  onSuccess: () =>
                    notify.default({
                      title: intl.formatMessage({
                        id: 'containers.message.ruleDeleteSuccess',
                      }),
                    }),
                  onError: () =>
                    notify.error({
                      title: intl.formatMessage({
                        id: 'containers.message.ruleDeleteFailed',
                      }),
                    }),
                }
              );
            },
          },
        ],
        id: toastId,
      });
    },
    mutationFn: ({ rule, queue }: { rule: QueueRule; queue?: Queue }) => {
      return api.request(
        endpoints.rules.create({
          name: `${rule.name} (${intl.formatMessage({
            id: 'features.queueSettings.rules.list.duplicate.suffix',
          })})`,
          description: rule.description,
          enabled: rule.enabled,
          triggerCondition: rule.triggerCondition,
          queues: queue ? [queue.url] : rule.queues,
          actions: rule.actions.map(action => ({
            ...action,
            id: uuidv4(),
          })),
        })
      );
    },
  });
};
